import { queue } from 'async';

import { TransferConfig } from '../../config/transfer-config';

import Logger from '@module/logger';

export class ChunksQueue {
  constructor({
    processChunkTask,
    concurrency = TransferConfig.queue.concurrency,
    retryInterval = TransferConfig.upload.retry.interval,
  } = {}) {
    // Create a queue object that will manage the parallel chunk uploads.
    // processTask function will be executed for every chunk.
    this.chunksQueue = queue(processChunkTask, concurrency);

    this.aborted = false;
    this.retryInterval = retryInterval;
  }

  push(chunk) {
    Logger.log(`[${chunk.file.name}] Queue part #${chunk.partNumber}`);
    return this.chunksQueue
      .pushAsync(chunk)
      .then(() => chunk)
      .catch((error) => this.retry(error, chunk));
  }

  retry(error, chunk) {
    Logger.log(`[${chunk.file.name}] Error in #${chunk.partNumber}: ${error}`);

    // Chunk upload failed. Check if we can try to upload it again.
    if (chunk.canRetry()) {
      Logger.log(
        `[${chunk.file.name}] Retry part #${chunk.partNumber} attempt #${chunk.attempts}`
      );

      return new Promise((resolve, reject) => {
        // Try again after a given amount of time
        setTimeout(() => {
          if (this.aborted) return;

          this.push(chunk).then(resolve).catch(reject);
        }, this.retryInterval);
      });
    }

    throw error;
  }

  // Pauses the processing of tasks until resume() is called.
  // It also aborts outgoing network requests
  pause() {
    Logger.log('Chunks queue has been paused.');
    this.chunksQueue.pause();
    this.chunksQueue.workersList().forEach(({ data: chunk }) => {
      // chunk.abort will cancel the outgoing network request,
      // reject the promise and queue the chunk again
      chunk.abort();
    });
  }

  // Resumes the processing of queued tasks when the queue is paused.
  resume() {
    Logger.log('Chunks queue has been resumed.');
    this.chunksQueue.resume();
  }

  // Empties remaining tasks from the queue forcing it to go idle.
  // No more tasks should be pushed to the queue after calling this function
  abort() {
    Logger.log(`Cleaning the queue!`);

    this.aborted = true;
    this.chunksQueue.kill();
  }

  saturated(callback) {
    this.chunksQueue.saturated(callback);
  }
}
